import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCanonicalAffiliateName } from "../../lib/affiliateIdentity";

const HomePage = () => {
  const { user, isAdmin, isMediaBuyer, loading, isRoleLoading, profile } =
    useAuth();

  console.log("🏠 HomePage render - State check:", {
    user: user?.email,
    isAdmin,
    isMediaBuyer,
    loading,
    isRoleLoading,
    profileLoaded: !!profile,
  });

  // CRITICAL: Wait for both auth loading AND role loading to complete
  if (loading || isRoleLoading) {
    console.log("⏳ HomePage - Still loading, showing spinner...", {
      loading,
      isRoleLoading,
    });
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-white text-sm">Loading user roles...</p>
        </div>
      </div>
    );
  }

  // Must have user to continue
  if (!user) {
    console.log("❌ HomePage - No user, redirecting to login");
    return <Navigate to="/login" />;
  }

  // Enhanced logging with profile data
  console.log("🔍 HomePage - ROLE DETERMINATION:", {
    userEmail: user?.email,
    isAdmin,
    isMediaBuyer,
    profileData: profile
      ? {
          is_admin: profile.is_admin,
          user_type: profile.user_type,
          username: profile.username,
        }
      : "No profile loaded",
  });

  // ADDITIONAL SAFETY: Ensure profile is actually loaded before making decisions
  if (!profile) {
    console.log("⚠️ HomePage - Profile not loaded yet, showing loading...");
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-white text-sm">Loading profile data...</p>
        </div>
      </div>
    );
  }

  // ===== ROLE-BASED REDIRECT PRIORITY SYSTEM =====

  // PRIORITY 1: ADMIN (HIGHEST) - Always redirect to admin dashboard
  // Even if user is also affiliate or media buyer, admin takes precedence
  if (isAdmin) {
    console.log("🎯 PRIORITY 1 - ADMIN detected:", {
      isAdmin,
      profileAdmin: profile.is_admin,
      profileUserType: profile.user_type,
      decision: "→ /dashboard",
    });
    return <Navigate to="/dashboard" replace />;
  }

  // PRIORITY 2: MEDIA BUYER (MEDIUM) - Only if NOT admin
  if (isMediaBuyer && !isAdmin) {
    const username = getCanonicalAffiliateName(profile, user);
    console.log("🎯 PRIORITY 2 - MEDIA BUYER detected:", {
      isMediaBuyer,
      isAdmin,
      profileUserType: profile.user_type,
      username,
      decision: `→ /media-buyer/${username}`,
    });
    return <Navigate to={`/media-buyer/${username}`} replace />;
  }

  // PRIORITY 3: AFFILIATE (LOWEST) - Default for regular users
  const affiliateName = getCanonicalAffiliateName(profile, user);
  console.log("🎯 PRIORITY 3 - AFFILIATE (default):", {
    isAdmin,
    isMediaBuyer,
    profileUserType: profile.user_type,
    affiliateName,
    decision: `→ /affiliate/${affiliateName}`,
  });
  return <Navigate to={`/affiliate/${affiliateName}`} replace />;
};

export default HomePage;
